import { CommonModule, DOCUMENT } from '@angular/common';
import { A11yModule } from '@angular/cdk/a11y';
import {
  Component,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  Renderer2,
  SimpleChanges,
} from '@angular/core';

// Reusable modal primitive (CONNECT 2.0 design system, Spec 2 Task 6). This is
// the single primitive that Spec 8 later migrates the ~30 ad-hoc modals onto,
// so behaviour is centralized here once.
//
// Content projection (documented scheme):
//   [modal-header] → header slot (also labels the dialog for a11y)
//   [modal-footer] → footer slot (actions)
//   default / [modal-body] → body slot (scrollable content)
//
// A11y + behaviour handled here (consumers get it for free):
//   - role="dialog" + aria-modal, labelled by the projected header or [ariaLabel]
//   - focus trap while open (CDK cdkTrapFocus) with focus RETURN on close
//   - ESC closes, backdrop click closes, body scroll-lock while open
//   - nothing rendered in the DOM while closed (structural @if)
//   - responsive: centered dialog ≥ md, full-screen bottom-sheet < sm
//
// The (close) output only signals intent — the parent owns the [open] state
// and flips it to false. This keeps the primitive stateless w.r.t. open/closed.
let modalHeaderSeq = 0;

// Shared (module-level) scroll-lock state. Multiple modals can be open at
// once (e.g. a confirm dialog stacked on a form modal); the lock must be
// reference-counted across ALL instances rather than saved/restored per
// instance, or the first modal to close would prematurely unlock the body
// (or the last one to close would leak `overflow: hidden` forever).
let openModalCount = 0;
let savedBodyOverflow: string | null = null;

@Component({
  standalone: true,
  selector: 'app-modal',
  imports: [CommonModule, A11yModule],
  templateUrl: './modal.component.html',
  styleUrl: './modal.component.scss',
})
export class ModalComponent implements OnChanges, OnDestroy {
  // Whether the modal is shown. Parent-owned — the primitive never mutates it.
  @Input() open = false;

  // Explicit accessible name. When provided it wins over the projected header
  // as the dialog's label (use it when the header has no plain-text title).
  @Input() ariaLabel?: string;

  // Clicking the backdrop requests close. Disable for flows where an explicit
  // action is required to dismiss.
  @Input() closeOnBackdrop = true;

  // Render the header's built-in "×" close affordance. Kept as a distinct
  // control so Spec 8 / Task 9 can attach the `{name}-close` data-testid here.
  @Input() showCloseButton = true;

  // QA hook (CONNECT 2.0 data-testid convention, Spec 2 Task 9). A stable,
  // kebab-case modal name, e.g. `decline-quote`. Drives `data-testid` on this
  // component's own "×" close button as `{name}-close`. The modal cannot own
  // confirm/cancel testids itself — those controls are consumer-projected
  // into `[modal-footer]` — so consumers apply `{name}-confirm` /
  // `{name}-cancel` to their own projected buttons (see
  // `src/app/shared/testid/README.md`; this is what Spec 8's ~30-modal
  // migration does). When `name` is unset, no testid is rendered at all.
  @Input() name?: string;

  // Width variant for the dialog. 'md' (default, 520px) suits confirmations;
  // 'lg' (720px) / 'xl' (960px) widen it for document-heavy modals such as the
  // ATP signing view. At ≤sm all variants collapse to the full-width sheet.
  @Input() size: 'md' | 'lg' | 'xl' = 'md';

  @Output() close = new EventEmitter<void>();

  // Stable id so aria-labelledby can point at the projected header.
  readonly headerId = `app-modal-header-${modalHeaderSeq++}`;

  // Whether THIS instance currently holds a lock, so a double-lock (repeated
  // `open` change) or double-unlock (e.g. `ngOnDestroy` after a normal close)
  // doesn't double-count against the shared `openModalCount`.
  private locked = false;

  constructor(
    private readonly renderer: Renderer2,
    @Inject(DOCUMENT) private readonly document: Document,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['open']) {
      this.applyScrollLock(this.open);
    }
  }

  ngOnDestroy(): void {
    // Never leave the page scroll-locked if the modal is torn down while open.
    this.applyScrollLock(false);
  }

  onBackdropClick(): void {
    if (this.closeOnBackdrop) {
      this.requestClose();
    }
  }

  onEscape(event: Event): void {
    event.stopPropagation();
    this.requestClose();
  }

  requestClose(): void {
    this.close.emit();
  }

  // `{name}-close`, or `null` (no attribute rendered) when `name` is unset.
  get closeTestId(): string | null {
    return this.name ? `${this.name}-close` : null;
  }

  // Toggle body scroll-lock. Reference-counted across ALL modal instances
  // (see the module-level `openModalCount` / `savedBodyOverflow` above) so
  // simultaneously-open modals don't corrupt each other's save/restore.
  private applyScrollLock(lock: boolean): void {
    const body = this.document.body;
    if (!body) {
      return;
    }

    if (lock) {
      if (this.locked) {
        return;
      }
      this.locked = true;
      if (openModalCount === 0) {
        savedBodyOverflow = body.style.overflow || null;
        this.renderer.setStyle(body, 'overflow', 'hidden');
      }
      openModalCount++;
    } else {
      if (!this.locked) {
        return;
      }
      this.locked = false;
      openModalCount = Math.max(0, openModalCount - 1);
      if (openModalCount === 0) {
        if (savedBodyOverflow) {
          this.renderer.setStyle(body, 'overflow', savedBodyOverflow);
        } else {
          this.renderer.removeStyle(body, 'overflow');
        }
        savedBodyOverflow = null;
      }
    }
  }
}
